import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useQuery } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system";
import LoadingWheel from "../shared/LoadingWheel";
import { UKVDResponse } from "../../models/UKVDResponse";
import apiConfig from "../../utils/apiConfig";


interface TrimComboboxProps {
  onTrimSelect: (trim: string) => void;
  currentValue?: string;
  label?: string;
  make?: string;
  model?: string;
}

export function TrimCombobox({ onTrimSelect, currentValue, label, make, model }: TrimComboboxProps) {
  const { get } = useApiHelper(apiConfig("TrimCombobox"));
  const [selectedTrim, setSelectedTrim] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const previousKeyRef = useRef<string | undefined>(`${make}|${model}`);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const enabled = Boolean(make && model);

  // Reuse the existing makes/models lookup — each UKVD entry carries
  // `modelVariantDescription`, which we filter by the selected model and
  // dedupe to produce the trim list.
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["model", "lookup", make],
    queryFn: () =>
      get<DefaultResponseWithData<UKVDResponse[]>>(
        `/utilities/lookup/makes${make ? `?make=${encodeURIComponent(make)}` : ''}`
      ),
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    enabled: Boolean(make),
  });

  const trims = useMemo(() => {
    if (!model) return [];

    const modelNeedle = model.toLowerCase().trim();
    const variants = (data?.data ?? [])
      .filter((entry) => entry.range?.toLowerCase().trim() === modelNeedle)
      .map((entry) => entry.modelVariantDescription?.trim())
      .filter((v): v is string => Boolean(v));

    const unique = Array.from(new Set(variants)).sort((a, b) => a.localeCompare(b));

    if (!debouncedSearch) return unique;

    const needle = debouncedSearch.toLowerCase();
    return unique.filter((trim) => trim.toLowerCase().includes(needle));
  }, [data, debouncedSearch, model]);

  useEffect(() => {
    const key = `${make}|${model}`;
    if (previousKeyRef.current !== key) {
      setSelectedTrim(null);
      setSearchTerm("");
      previousKeyRef.current = key;
      return;
    }

    if (isLoading || currentValue === undefined) return;

    if (currentValue === "") {
      setSelectedTrim(null);
      setSearchTerm("");
      return;
    }

    if (currentValue !== selectedTrim) {
      setSelectedTrim(currentValue);
      setSearchTerm(currentValue);
    }
  }, [make, model, currentValue, data, isLoading, selectedTrim]);


  return (
    <div>
      {label &&
        <label>
          <span className="text-sm font-medium text-black">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        value={selectedTrim ?? null}
        onChange={(trim: string) => {
          setSelectedTrim(trim);
          onTrimSelect(trim);
        }}
        key={`${make}|${model}`}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400 rounded-md disabled:bg-gray-100"
            onChange={(event) => setSearchTerm(event.target.value)}
            displayValue={(trim: string) => trim || searchTerm}
            placeholder={enabled ? "Search trims..." : "Select model first"}
            autoComplete="off"
            disabled={!enabled}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {enabled && (isLoading || isFetching) ? (
              <div className="flex items-center justify-center h-5 w-5">
                <LoadingWheel />
              </div>
            ) : (
              <ChevronUpDownIcon
                className="size-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {trims && trims.length > 0 ? (
              trims.slice(0, 100).map((trim) => (
                <ComboboxOption
                  key={trim}
                  value={trim}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {trim}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                            }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                {enabled ? "Nothing found." : "Select a model first."}
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox >
    </div >
  );
}
